/**
 * BlockersList Component
 *
 * Displays critical issues and blockers preventing AEO generation.
 * Sorted by severity with actionable links.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, Info, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Blocker {
  severity: 'critical' | 'high' | 'medium';
  message: string;
  dimension: string;
}

interface Dimensions {
  [key: string]: {
    action?: {
      url: string | null;
      label: string;
    };
  };
}

interface BlockersListProps {
  blockers: Blocker[];
  dimensions: Dimensions;
}

const severityConfig = {
  critical: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    borderColor: 'border-red-200 dark:border-red-800',
    badgeVariant: 'destructive' as const
  },
  high: {
    icon: AlertTriangle,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    badgeVariant: 'secondary' as const
  },
  medium: {
    icon: Info,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    badgeVariant: 'outline' as const
  }
};

const dimensionLabels: Record<string, string> = {
  productsUploaded: 'Products',
  productsCategorized: 'Categorization',
  categoriesResearched: 'Research',
  productsEnriched: 'Enrichment',
  suspiciousValues: 'Quality Issues',
  categoryDemand: 'Demand',
  buyerPersonas: 'Personas',
  perceivedBenefits: 'Benefits',
  embeddingsDone: 'Embeddings',
  questionWheels: 'Question Wheels',
  aeoGenerated: 'AEO'
};

export function BlockersList({ blockers, dimensions }: BlockersListProps) {
  const navigate = useNavigate();

  if (blockers.length === 0) {
    return (
      <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-700 dark:text-green-300">No Critical Blockers</p>
              <p className="text-sm text-green-600 dark:text-green-400">
                Your site is progressing well toward AEO readiness.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort blockers by severity
  const sortedBlockers = [...blockers].sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2 };
    return order[a.severity] - order[b.severity];
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          Issues to Address
          <Badge variant="destructive" className="ml-2">
            {blockers.filter(b => b.severity === 'critical').length} Critical
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sortedBlockers.map((blocker, idx) => {
          const config = severityConfig[blocker.severity];
          const Icon = config.icon;
          const dimension = dimensions[blocker.dimension];
          const actionUrl = dimension?.action?.url;

          return (
            <div
              key={idx}
              className={`p-3 rounded-lg border ${config.bgColor} ${config.borderColor} flex items-start gap-3`}
            >
              <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${config.color}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={config.badgeVariant} className="text-xs">
                    {blocker.severity.toUpperCase()}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {dimensionLabels[blocker.dimension] || blocker.dimension}
                  </span>
                </div>
                <p className="text-sm">{blocker.message}</p>
              </div>
              {actionUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(actionUrl)}
                  className="flex-shrink-0"
                >
                  Fix
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
